export type Step1Draft = {
  fullName: string
  email: string
  phone: string
  termsAccepted: boolean
}

export type AddressDraft = {
  zip_code: string
  street: string
  number: string
  complement?: string
  neighborhood: string
  city: string
  state: string
}

export type ProfileDraft = {
  document: string
  birthdate: string
  gender: string
  maritalStatus: string
  profession: string
  additionalPhone?: string
  address: AddressDraft
  profilePicture?: string | null
}

export type RegistrationDraft = {
  step1?: Step1Draft
  profile?: ProfileDraft
  step1Completed?: boolean
  step2Completed?: boolean
  updatedAt?: number
}

const REGISTRATION_STORAGE_KEY = "registrationDraft"
const RESERVATION_REDIRECT_KEY = "reservationRedirect"

export const normalizeDigits = (value: string) => value.replace(/\D/g, "")

export const isValidEmail = (value: string) => {
  const email = value.trim()
  if (!email) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export const isValidPhone = (value: string) => {
  const digits = normalizeDigits(value)
  if (digits.length !== 10 && digits.length !== 11) return false
  const ddd = digits.slice(0, 2)
  if (ddd === "00") return false
  return true
}

export const isValidPassword = (value: string) => {
  if (!value || value.length < 8) return false
  if (!/[a-z]/.test(value)) return false
  if (!/[A-Z]/.test(value)) return false
  if (!/\d/.test(value)) return false
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) return false
  return true
}

export const isValidCPF = (value: string) => {
  const cpf = normalizeDigits(value)
  if (cpf.length !== 11) return false
  if (/^(\d)\1{10}$/.test(cpf)) return false
  const calcCheck = (base: string, factor: number) => {
    let total = 0
    for (const digit of base) {
      total += parseInt(digit, 10) * factor
      factor -= 1
    }
    const mod = total % 11
    return mod < 2 ? 0 : 11 - mod
  }
  const firstCheck = calcCheck(cpf.slice(0, 9), 10)
  const secondCheck = calcCheck(cpf.slice(0, 10), 11)
  return cpf.endsWith(`${firstCheck}${secondCheck}`)
}

export const isValidCNPJ = (value: string) => {
  const cnpj = normalizeDigits(value)
  if (cnpj.length !== 14) return false
  if (/^(\d)\1{13}$/.test(cnpj)) return false

  const calcCheck = (base: string, factors: number[]) => {
    const total = base.split("").reduce((sum, digit, index) => sum + Number(digit) * factors[index], 0)
    const mod = total % 11
    return mod < 2 ? 0 : 11 - mod
  }

  const firstCheck = calcCheck(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  const secondCheck = calcCheck(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
  return cnpj.endsWith(`${firstCheck}${secondCheck}`)
}

export const isValidCEP = (value: string) => {
  const digits = normalizeDigits(value)
  return digits.length === 8
}

export const isValidBRDate = (value: string) => {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false
  const [day, month, year] = value.split("/").map(Number)
  if (!day || !month || !year) return false
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year) return false
  if (date.getMonth() !== month - 1) return false
  if (date.getDate() !== day) return false
  return true
}

export const toISODate = (value: string) => {
  if (!isValidBRDate(value)) return ""
  const [day, month, year] = value.split("/")
  return `${year}-${month}-${day}`
}

export const fromISODate = (value?: string | null) => {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const day = String(date.getDate()).padStart(2, "0")
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export const getRegistrationDraft = (): RegistrationDraft => {
  try {
    const raw = localStorage.getItem(REGISTRATION_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as RegistrationDraft) : {}
  } catch {
    return {}
  }
}

export const saveRegistrationDraft = (draft: RegistrationDraft) => {
  try {
    localStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(draft))
  } catch {
  }
}

export const updateRegistrationDraft = (partial: Partial<RegistrationDraft>) => {
  const current = getRegistrationDraft()
  const next = {
    ...current,
    ...partial,
    updatedAt: Date.now()
  }
  saveRegistrationDraft(next)
  return next
}

export const clearRegistrationDraft = () => {
  try {
    localStorage.removeItem(REGISTRATION_STORAGE_KEY)
  } catch {
  }
}

export const setReservationRedirect = (path: string) => {
  try {
    localStorage.setItem(RESERVATION_REDIRECT_KEY, path)
  } catch {
  }
}

export const getReservationRedirect = () => {
  try {
    return localStorage.getItem(RESERVATION_REDIRECT_KEY)
  } catch {
    return null
  }
}

export const clearReservationRedirect = () => {
  try {
    localStorage.removeItem(RESERVATION_REDIRECT_KEY)
  } catch {
  }
}

export const isStep1Complete = (user: any, draft?: RegistrationDraft) => {
  if (user?.email) return true
  return Boolean(draft?.step1?.email)
}

export const isProfileComplete = (user: any, draft?: RegistrationDraft) => {
  const profile = draft?.profile
  const address = profile?.address
  const hasDocument = Boolean(profile?.document || user?.document)
  const hasBirthdate = Boolean(profile?.birthdate || user?.birthdate)
  const hasGender = Boolean(profile?.gender || user?.gender)
  const hasProfession = Boolean(profile?.profession)
  const hasMaritalStatus = Boolean(profile?.maritalStatus)
  const hasProfilePicture = Boolean(profile?.profilePicture || user?.profile_picture_url)
  const hasAddress = Boolean(
    address?.zip_code &&
    address?.street &&
    address?.number &&
    address?.neighborhood &&
    address?.city &&
    address?.state
  )
  return Boolean(hasDocument && hasBirthdate && hasGender && hasProfession && hasMaritalStatus && hasAddress && hasProfilePicture)
}

export const getRegistrationProgress = (user: any, draft?: RegistrationDraft) => {
  const step1 = isStep1Complete(user, draft)
  const step2 = isProfileComplete(user, draft)
  if (step2) return 100
  if (step1) return 50
  return 0
}
